import { createSelector } from './selectorWrapper'
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { isType } from '../utils/isType'
import { flattenArray, isEmpty } from '../utils/utils'
import { pageSelectors, simpleSelectors } from './basicState'
import { getFirstPlaybackData, isPlaybackActionTreeRight } from '../utils/playbackActionHelper'
import { isDownloadActionTreeRight } from '../utils/downloadActionHelper'
import { CollectionDetail, SECOND_EXPANDER_THRESHOLD } from '../reducers/collections'
import { TitleIdToAction } from '../reducers/action'
import { isWatched } from '../utils/getPlaybackProgress'

// Utilities
const getChildMessages = (children: WpsRuntime.atv.wps.PurchaseActionList) =>
  children
    .filter(child => !isEmpty(child.displayMessages))
    .map(child => child.displayMessages)

export const btfActions = createSelector(
  [simpleSelectors.actionBtf, pageSelectors.collections],
  (allBtfActions: TitleIdToAction, collections: CollectionDetail[] = []) =>
    Array.from(new Set(flattenArray(collections.map(c => c.titleIds)))).reduce(
      (acc, titleId) => {
        const action = allBtfActions[titleId]
        if (action) {
          acc[titleId] = action
        }
        return acc
      },
      {} as TitleIdToAction
    )
)

export const acquisitionActionsSelector = createSelector(
  pageSelectors.actionAtf,
  (actionFragment?: WpsRuntime.atv.wps.ActionFragmentModelProps) => actionFragment && actionFragment.acquisitionActions
)

export const playbackActionsSelector = createSelector(
  pageSelectors.actionAtf,
  (actionFragment?: WpsRuntime.atv.wps.ActionFragmentModelProps) => actionFragment && actionFragment.playbackActions
)

export const downloadActionSelector = createSelector(
         pageSelectors.actionAtf,
         (actionFragment?: WpsRuntime.atv.wps.ActionFragmentModelProps) =>
           actionFragment && (actionFragment.downloadActions as WpsRuntime.atv.wps.DownloadActionsModel)
       )

export const notificationActionsSelector = createSelector(
  pageSelectors.actionAtf,
  (actionFragment?: WpsRuntime.atv.wps.ActionFragmentModelProps) => actionFragment && actionFragment.notificationActions
)

export const disneyActionSelector = createSelector(
  pageSelectors.actionAtf,
  (actionFragment?: WpsRuntime.atv.wps.ActionFragmentModelProps) => actionFragment && actionFragment.disneyAction
)

/* Returns the number of episodes seen up to the threshold */
export const episodesSeenSelector = createSelector(
  [simpleSelectors.actionBtf, pageSelectors.collections],
  (allBtfActions: TitleIdToAction, collections: CollectionDetail[] = []) =>
    flattenArray(collections
      .filter((col) => col.collectionType === 'episodes')
      .map((col) => col.titleIds))
      .slice(0, SECOND_EXPANDER_THRESHOLD)
      .reduce((count, titleId) => {
        const action = allBtfActions[titleId]
        if (!(action && action.playbackActions)) {
          return count
        }
        const playbackData = getFirstPlaybackData(action.playbackActions)
        return playbackData && isWatched(playbackData.progress) ? count + 1 : count
      }, 0)
)

export interface TitleIdToAvailable {
  [titleId: string]: boolean
}

export const episodesAvailableSelector = createSelector(
  [simpleSelectors.actionBtf, pageSelectors.collections],
  (allBtfActions: TitleIdToAction, collections: CollectionDetail[] = []) => {
    return flattenArray(collections
      .filter((col) => col.collectionType === 'episodes')
      .map((col) => col.titleIds))
      .reduce((acc, titleId) => {
          const action = allBtfActions[titleId]
          acc[titleId] = action && action.playbackActions && getFirstPlaybackData(action.playbackActions) !== undefined
          return acc
        }, {} as TitleIdToAvailable
      )
  }
)

// ================================= Acquisition Actions =======================================

// SVOD

export const isSubscribeActionTreeRight = (
  svodWinners?: WpsRuntime.atv.wps.SubscribeActionTreeModelProps
): svodWinners is WpsRuntime.atv.wps.SubscribeActionTreeRight =>
  isType(svodWinners, 'atv.wps#SubscribeActionTreeRight')

export const svodActionTreeLeafSelector = createSelector(
  acquisitionActionsSelector,
  (acquisitionActions?: WpsRuntime.atv.wps.AcquisitionActionsModelProps) => {
    const svodWinners = acquisitionActions && acquisitionActions.svodWinners
    return isSubscribeActionTreeRight(svodWinners) ? svodWinners : undefined
  }
)

export const svodWinnerChildrenSelector = createSelector(
  svodActionTreeLeafSelector,
  (svodWinners?: WpsRuntime.atv.wps.SubscribeActionTreeRightProps) => svodWinners && svodWinners.children
)

export const svodProblemsSelector = createSelector(
  svodWinnerChildrenSelector,
  (svodWinnerChildren?: WpsRuntime.atv.wps.SubscribeActionList) => svodWinnerChildren &&
    flattenArray(svodWinnerChildren.map(child => child.problems))
)

export const svodProblemsMessagesSelector = createSelector(
  svodProblemsSelector,
  (problems?: WpsRuntime.atv.wps.SubscriptionProblemList) => problems &&
    problems.map(problem => problem.message)
)

export const svodMetadataSelector = createSelector(
  svodActionTreeLeafSelector,
  (svodWinners?: WpsRuntime.atv.wps.SubscribeActionTreeRightProps) => svodWinners && svodWinners.metadata
)

export const svodMetadataBehaviourSelector = createSelector(
  svodMetadataSelector,
  (metadata?: WpsRuntime.atv.wps.ActionMetadataProps) => metadata && metadata.behaviour
)


// TVOD

// Until now, there is no use case for tvodWinners to be a PurchaseActionTreeLeft. This is not supported in Nody2, so in
// such a case we get an error from WPS (AVWebDetailPage-3100) and we do not print anything.
export const isPurchaseActionTreeRight = (
  tvodWinners?: WpsRuntime.atv.wps.PurchaseActionTreeModelProps
): tvodWinners is WpsRuntime.atv.wps.PurchaseActionTreeRight =>
  isType(tvodWinners, 'atv.wps#PurchaseActionTreeRight')

export const tvodWinnersSelector = createSelector(
  acquisitionActionsSelector,
  (acquisitionActions?: WpsRuntime.atv.wps.AcquisitionActionsModelProps) =>
    acquisitionActions && isPurchaseActionTreeRight(acquisitionActions.tvodWinners)
      ? acquisitionActions.tvodWinners
      : undefined
)

export const tvodWinnerChildrenSelector = createSelector(
  tvodWinnersSelector,
  (tvodWinners?: WpsRuntime.atv.wps.PurchaseActionTreeRightProps) => tvodWinners && tvodWinners.children
)

export const tvodMetadataSelector = createSelector(
  tvodWinnersSelector,
  (tvodWinners?: WpsRuntime.atv.wps.PurchaseActionTreeRightProps) => tvodWinners && tvodWinners.metadata
)

export const tvodMetadataMessagesSelector = createSelector(
  tvodMetadataSelector,
  (tvodMetadata?: WpsRuntime.atv.wps.ActionMetadataProps) => tvodMetadata && tvodMetadata.messages
)

export const tvodMessagesSelector = createSelector(
  tvodWinnerChildrenSelector,
  (tvodWinnerChildren?: WpsRuntime.atv.wps.PurchaseActionList) =>
    tvodWinnerChildren && flattenArray(getChildMessages(tvodWinnerChildren))
)

// MWTW

export const mwtwSelector = createSelector(
  acquisitionActionsSelector,
  (acquisitionActions?: WpsRuntime.atv.wps.AcquisitionActionsModelProps) =>
    acquisitionActions && acquisitionActions.moreWaysToWatch
)


// Pre orders

export const acceptedPreorderSelector = createSelector(
  acquisitionActionsSelector,
  (acquisitionActions?: WpsRuntime.atv.wps.AcquisitionActionsModelProps) =>
    acquisitionActions && acquisitionActions.acceptedPreorder
)

// ================================= Playback Actions =======================================

export const playbackMetadataSelector = createSelector(
  playbackActionsSelector,
  (playbackActions?: WpsRuntime.atv.wps.PlaybackActionsModelProps) =>
    playbackActions && playbackActions.main && isPlaybackActionTreeRight(playbackActions.main) && playbackActions.main.metadata || undefined
)

export const playbackChildrenSelector = createSelector(
  playbackActionsSelector,
  (playbackActions?: WpsRuntime.atv.wps.PlaybackActionsModelProps) =>
    playbackActions && playbackActions.main && isPlaybackActionTreeRight(playbackActions.main) && playbackActions.main.children || undefined
)

export const playbackFirstChildSelector = createSelector(
  playbackChildrenSelector,
  (playbackDataList?: WpsRuntime.atv.wps.PlaybackActionList) =>
    playbackDataList && playbackDataList[0]
)


// ================================= Download Actions =======================================

export const downloadChildrenSelector = createSelector(
  downloadActionSelector,
  (downloadActions?: WpsRuntime.atv.wps.DownloadActionsModelProps) =>
    downloadActions && downloadActions.main && isDownloadActionTreeRight(downloadActions.main) && downloadActions.main.children || undefined
)

export const downloadFirstChildSelector = createSelector(
  downloadChildrenSelector,
  (downloadDataList?: WpsRuntime.atv.wps.DownloadActionList) =>
    downloadDataList && downloadDataList[0]
)
